import * as React from 'react';
import { useSelector } from 'react-redux';
import Button from 'antd/lib/button';
import Card from 'antd/lib/card';
import Empty from 'antd/lib/empty';
import Input from 'antd/lib/input';
import Col from 'antd/lib/col';
import Row from 'antd/lib/row';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import Upload from 'antd/lib/upload';
import { CheckOutlined, CloseOutlined, EditOutlined, PlusOutlined, SortAscendingOutlined } from '@ant-design/icons';
import { AppComponentProps } from '../../typescriptHelpers';
import { RootState } from '../../reducers/reducers';
import { selectDestinationInfoByDestinationStepConfigId } from '../../selectors/selectors';
import { displayStringForDestination, displayTextForAppPlatform } from '../../helpers/displayHelpers';
import { Destination } from '@/types/destinations';
import { antBlue, antGrey, colorWithAlpha, runwayColors } from '../styles/globalStyles';
import { DestinationIcon } from '../sidebar/shared/components';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import { DestinationUpdatable } from '@/actions/updatable';
import { globalMessage } from '@/utils/globalMessage';

const DESTINATION_CARDS_PER_ROW = 4;

const destinationDisplayName = (destination: Destination) =>
    displayStringForDestination(destination.platform, destination.appStoreType, destination.name, destination.appName);

const destinationKeywords = (destination: Destination) => {
    return [
        destinationDisplayName(destination),
        displayTextForAppPlatform(destination.platform, destination.appStoreType),
        destination.appName,
        ...(destination.tags ?? []),
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
};

interface DestinationCardProps {
    destination: Destination;
    onEditClicked: (destination: Destination) => void;
}

const DestinationCard: React.FC<DestinationCardProps> = ({ destination, onEditClicked }) => {
    const name = destinationDisplayName(destination);

    return (
        <Card
            bodyStyle={{ padding: '10px 14px' }}
            style={{
                width: 165,
                borderColor: antGrey.grey4,
                borderRadius: 6,
                backgroundColor: antGrey.grey1,
                boxShadow: `0 8px 18px ${colorWithAlpha(antGrey.grey5, 0.22)}`,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                <DestinationIcon destination={destination} size={28} />
                <Text
                    style={{
                        flex: 1,
                        color: antGrey.grey8,
                        fontSize: 14,
                    }}
                    ellipsis={{ tooltip: name }}
                >
                    {name}
                </Text>
                <Button
                    size="middle"
                    icon={<EditOutlined />}
                    aria-label={`Edit ${name}`}
                    onClick={(_) => {
                        onEditClicked(destination);
                    }}
                    style={{
                        color: antGrey.grey6,
                        borderColor: antGrey.grey4,
                        flex: '0 0 auto',
                    }}
                />
            </div>
        </Card>
    );
};

interface DestinationEditorProps {
    destination: Destination;
    connectorLeft: number;
    isSaving: boolean;
    onCancel: () => void;
    onApply: (destination: Destination) => void | Promise<void>;
}

const DestinationEditor: React.FC<DestinationEditorProps> = ({
    destination,
    connectorLeft,
    isSaving,
    onCancel,
    onApply,
}) => {
    const [name, setName] = React.useState(destinationDisplayName(destination));
    const [tags, setTags] = React.useState<string[]>(destination.tags ?? []);
    const [newTag, setNewTag] = React.useState('');
    const [showNewTagInput, setShowNewTagInput] = React.useState(false);

    React.useEffect(() => {
        setName(destinationDisplayName(destination));
        setTags(destination.tags ?? []);
        setNewTag('');
        setShowNewTagInput(false);
    }, [destination]);

    const addTag = () => {
        const normalizedTag = newTag.trim();
        if (!normalizedTag || tags.includes(normalizedTag)) {
            setNewTag('');
            setShowNewTagInput(false);
            return;
        }

        setTags([...tags, normalizedTag]);
        setNewTag('');
        setShowNewTagInput(false);
    };

    return (
        <div
            style={{
                position: 'relative',
                width: 600,
                maxWidth: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                backgroundColor: antGrey.grey4,
                borderRadius: '0 6px 6px 6px',
                padding: '18px 20px',
                columnGap: 24,
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: -10,
                    left: connectorLeft,
                    width: 165,
                    height: 10,
                    backgroundColor: antGrey.grey4,
                }}
            />
            <Space
                direction="vertical"
                size={18}
                style={{
                    borderRight: `1px solid ${antGrey.grey4}`,
                    paddingRight: 24,
                    width: '100%',
                }}
            >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Destination name</Text>
                    <Input value={name} onChange={(e) => setName(e.target.value)} />
                </Space>

                <Space direction="vertical" size={8}>
                    <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Destination icon</Text>
                    <Upload.Dragger
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={() => false}
                        style={{
                            width: 112,
                            height: 112,
                            backgroundColor: runwayColors.white,
                        }}
                    >
                        <Space size={4}>
                            <PlusOutlined style={{ color: antGrey.grey6 }} />
                            <Text type="secondary">Upload</Text>
                        </Space>
                    </Upload.Dragger>
                </Space>

                <Space size={8}>
                    <Button icon={<CloseOutlined style={{ color: runwayColors.statusRed }} />} onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        loading={isSaving}
                        onClick={() =>
                            onApply({
                                ...destination,
                                name: name || destinationDisplayName(destination),
                                tags,
                            })
                        }
                    >
                        Apply
                    </Button>
                </Space>
            </Space>

            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Tags</Text>
                <Space size={8} wrap>
                    {tags.map((tag, _) => (
                        <Tag
                            key={tag}
                            closable
                            onClose={(event) => {
                                event.preventDefault();
                                setTags(tags.filter((existingTag) => existingTag !== tag));
                            }}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                marginRight: 0,
                                color: antGrey.grey8,
                                borderColor: antGrey.grey4,
                                backgroundColor: runwayColors.white,
                                fontSize: 14,
                            }}
                        >
                            <span
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: 2,
                                    // backgroundColor: tag.color, TODO: RUN-14911
                                }}
                            />
                            {tag}
                        </Tag>
                    ))}
                    {showNewTagInput ?
                        <Input
                            autoFocus
                            size="small"
                            value={newTag}
                            onChange={(event) => setNewTag(event.target.value)}
                            onBlur={addTag}
                            onPressEnter={addTag}
                            style={{ width: 120 }}
                        />
                    :   <Button type="text" icon={<PlusOutlined />} onClick={() => setShowNewTagInput(true)} />}
                </Space>
            </Space>
        </div>
    );
};

export const DestinationsSettings: React.FC<AppComponentProps> = ({ orgId, appId }) => {
    const [searchValue, setSearchValue] = React.useState('');
    const [shouldSortAlphabetically, setShouldSortAlphabetically] = React.useState(false);
    const [localDestinationEditsByStepConfigId, setLocalDestinationEditsByStepConfigId] = React.useState<
        Record<string, Destination>
    >({});
    const [selectedDestinationStepConfigId, setSelectedDestinationStepConfigId] = React.useState<string | null>(null);
    const [pendingUpdatedDestination, setPendingUpdatedDestination] = React.useState<Destination | null>(null);

    const destinationInfoByDestinationStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );

    const destinations = React.useMemo(() => {
        return Object.values(destinationInfoByDestinationStepConfigId).map(
            (destination) => localDestinationEditsByStepConfigId[destination.stepConfigId] ?? destination,
        );
    }, [destinationInfoByDestinationStepConfigId, localDestinationEditsByStepConfigId]);

    const selectedDestination =
        selectedDestinationStepConfigId ?
            destinations.find((destination) => destination.stepConfigId === selectedDestinationStepConfigId)
        :   undefined;
    const selectedDestinationId = selectedDestination?.destinationId ?? '';
    const destinationUpdateId = selectedDestinationId || pendingUpdatedDestination?.destinationId || '';
    const [
        updateDestinationDetails,
        updateDestinationDetailsIsLoading,
        updateDestinationDetailsResponseType,
        updateDestinationDetailsError,
    ] = useCallUpdatable(
        DestinationUpdatable.UpdateDestinationDetails,
        {
            orgId,
            appId,
            destinationId: destinationUpdateId,
        },
        destinationUpdateId,
    );

    React.useEffect(() => {
        if (pendingUpdatedDestination && updateDestinationDetailsResponseType) {
            globalMessage.success('Destination updated successfully.');
            setLocalDestinationEditsByStepConfigId((currentEdits) => ({
                ...currentEdits,
                [pendingUpdatedDestination.stepConfigId]: pendingUpdatedDestination,
            }));
            setSelectedDestinationStepConfigId(null);
            setPendingUpdatedDestination(null);
        }
    }, [pendingUpdatedDestination, updateDestinationDetailsResponseType]);

    React.useEffect(() => {
        if (pendingUpdatedDestination && updateDestinationDetailsError) {
            globalMessage.error(updateDestinationDetailsError);
            setPendingUpdatedDestination(null);
        }
    }, [pendingUpdatedDestination, updateDestinationDetailsError]);

    const displayedDestinations = React.useMemo(() => {
        const normalizedSearch = searchValue.trim().toLowerCase();
        const matchingDestinations =
            normalizedSearch ?
                destinations.filter((destination) => destinationKeywords(destination).includes(normalizedSearch))
            :   destinations;

        if (!shouldSortAlphabetically) return matchingDestinations;

        return [...matchingDestinations].sort((a, b) =>
            destinationDisplayName(a).localeCompare(destinationDisplayName(b)),
        );
    }, [destinations, searchValue, shouldSortAlphabetically]);

    const selectedDisplayedDestinationIndex =
        selectedDestinationStepConfigId ?
            displayedDestinations.findIndex(
                (destination) => destination.stepConfigId === selectedDestinationStepConfigId,
            )
        :   -1;

    const tags = React.useMemo(() => {
        const tags = destinations.reduce<string[]>((acc, destination) => {
            acc.push(...(destination.tags ?? []));
            return acc;
        }, []);

        return Array.from(new Set(tags)).sort((a, b) => a.localeCompare(b));
    }, [destinations]);

    return (
        <div
            style={{
                border: `1px solid ${antGrey.grey4}`,
                borderRadius: 8,
                backgroundColor: antGrey.grey2,
                padding: 20,
            }}
        >
            <Row gutter={[16, 24]} align="top">
                <Col xs={24} md={4}>
                    <Text style={{ color: antGrey.grey8, fontSize: 16 }}>Destinations</Text>
                </Col>

                <Col xs={24} md={14} xl={15}>
                    <Space direction="vertical" size={18} style={{ width: '100%' }}>
                        <Input.Search
                            allowClear
                            placeholder="Search by name, tag, keyword"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onSearch={setSearchValue}
                            style={{ maxWidth: 560 }}
                        />

                        {displayedDestinations.length > 0 ?
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 10,
                                }}
                            >
                                {displayedDestinations.map((destination, index) => (
                                    <React.Fragment key={destination.stepConfigId}>
                                        <DestinationCard
                                            destination={destination}
                                            onEditClicked={(destination) =>
                                                setSelectedDestinationStepConfigId(destination.stepConfigId)
                                            }
                                        />
                                        {selectedDestination &&
                                            selectedDisplayedDestinationIndex >= 0 &&
                                            (index + 1 === displayedDestinations.length ||
                                                (index + 1) % DESTINATION_CARDS_PER_ROW === 0) &&
                                            selectedDisplayedDestinationIndex >=
                                                index -
                                                    ((index + 1) % DESTINATION_CARDS_PER_ROW ||
                                                        DESTINATION_CARDS_PER_ROW) +
                                                    1 &&
                                            selectedDisplayedDestinationIndex <= index && (
                                                <DestinationEditor
                                                    destination={selectedDestination}
                                                    isSaving={updateDestinationDetailsIsLoading}
                                                    connectorLeft={
                                                        (selectedDisplayedDestinationIndex %
                                                            DESTINATION_CARDS_PER_ROW) *
                                                        (165 + 10)
                                                    }
                                                    onCancel={() => setSelectedDestinationStepConfigId(null)}
                                                    onApply={(updatedDestination) => {
                                                        const destinationId = updatedDestination.destinationId;
                                                        if (!destinationId || !updateDestinationDetails) {
                                                            globalMessage.error('Unable to update destination');
                                                            return;
                                                        }

                                                        updateDestinationDetails({
                                                            name: updatedDestination.name,
                                                            iconURL: updatedDestination.iconURL,
                                                            tags: updatedDestination.tags ?? [],
                                                        });
                                                        setPendingUpdatedDestination(updatedDestination);
                                                    }}
                                                />
                                            )}
                                    </React.Fragment>
                                ))}
                            </div>
                        :   <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No destinations found" />}
                    </Space>
                </Col>

                <Col xs={24} md={6} xl={5}>
                    <Space direction="vertical" size={24} style={{ width: '100%' }}>
                        <Button
                            icon={<SortAscendingOutlined style={{ color: antBlue.blue6 }} />}
                            onClick={() => setShouldSortAlphabetically(true)}
                            style={{
                                width: '174px',
                                height: 40,
                                borderColor: antGrey.grey4,
                                color: antGrey.grey8,
                                textAlign: 'left',
                            }}
                        >
                            Sort: Alphabetical
                        </Button>

                        <Space direction="vertical" size={10} style={{ width: '100%' }}>
                            <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Tags</Text>
                            <Space direction="vertical" size={6} align="start">
                                {tags.length > 0 ?
                                    tags.map((tag, _) => (
                                        <Tag
                                            key={tag}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                marginRight: 0,
                                                color: antGrey.grey8,
                                                borderColor: antGrey.grey4,
                                                backgroundColor: runwayColors.white,
                                                fontSize: 14,
                                            }}
                                        >
                                            <span
                                                style={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: 2,
                                                    // backgroundColor: tag.color, TODO: RUN-14911
                                                }}
                                            />
                                            {tag}
                                        </Tag>
                                    ))
                                :   <Text type="secondary">No tags</Text>}
                            </Space>
                        </Space>
                    </Space>
                </Col>
            </Row>
        </div>
    );
};
